import React, { useEffect, useState } from 'react';
import { DateFilter } from './filters/DateFilter';
import { Box, StarBorderIcon } from 'components';

import {
  getCitiesFromMarkets,
  getRolesFromMarkets,
  useIsMobile,
} from 'utils';
import { format } from 'date-fns';
import {
  FilterMenuButton,
  DesktopFilters,
  MobileFilters,
  MobileFilterMenu,
} from '.';
import { TrainingFormatFilter } from './filters/TrainingFormatFilter';
import { ListFilter } from './filters/ListFilter';

export const CareerDevFilterBar = ({ filterState, setFilterState, markets }) => {
  const isMobile = useIsMobile();
  const [tempFilterState, setTempFilterState] = useState({ ...filterState });
  const cities = getCitiesFromMarkets(markets);
  const roles = getRolesFromMarkets(markets);

  useEffect(() => {
    setTempFilterState(() => ({ ...filterState }));
  }, [filterState]);

  const resetState = (...keys) => {
    if (keys.length === 0) {
      keys = Object.keys(filterInitialState);
    }
    keys.forEach((key) => {
      setTempFilterState((fs) => ({ ...fs, [key]: filterInitialState[key] }));
      setFilterState((fs) => ({ ...fs, [key]: filterInitialState[key] }));
    });
  };

  return (
    <Box display="flex" alignItems="center" mt={3.5} mb={2}>
      {false && isMobile ? (
        <CareerDevMobileFilters
          tempFilterState={tempFilterState}
          setTempFilterState={setTempFilterState}
          resetState={resetState}
          cities={cities}
          roles={roles}
          setFilterState={setFilterState}
        />
      ) : (
        <CareerDevDesktopFilters
          filterState={filterState}
          setFilterState={setFilterState}
          tempFilterState={tempFilterState}
          setTempFilterState={setTempFilterState}
          resetState={resetState}
          cities={cities}
          roles={roles}
        />
      )}
    </Box>
  );
};

const CareerDevDesktopFilters = ({
  filterState,
  setFilterState,
  tempFilterState,
  setTempFilterState,
  resetState,
  cities,
  roles,
}) => {
  const dateChanged =
    +filterInitialState.date1 !== +filterState.date1 && filterState.date1;

  const citiesChanged = filterState.cities.length > 0;
  const rolesChanged = filterState.roles.length > 0;
  const locationChanged = filterState.location;

  const anyChanged =
    citiesChanged || rolesChanged || dateChanged || locationChanged;

  const menuProps = {
    onSave: () => setFilterState({ ...tempFilterState }),
    onCancel: () => setTempFilterState({ ...filterState }),
  };
  
  const filterProps = {
    filterState: tempFilterState,
    setFilterState: setTempFilterState,
  };

  return (
    <DesktopFilters allowReset={anyChanged} resetState={resetState}>
      <FilterMenuButton
        icon="Dates"
        label={
          dateChanged
            ? filterState.date2
              ? `${format(filterState.date1, 'MMM d')} - ${format(
                  filterState.date2,
                  'MMM d',
                )}`
              : format(filterState.date1, 'MMM d')
            : 'Dates'
        }
        changed={dateChanged}
        onReset={() => resetState('date1', 'date2')}
        {...menuProps}
      >
        <DateFilter {...filterProps} />
      </FilterMenuButton>
      <FilterMenuButton
        label="Roles"
        Icon={StarBorderIcon}
        changed={rolesChanged}
        onReset={() => {
          resetState('roles');
        }}
        {...menuProps}
      >
        <ListFilter stateKey="roles" items={roles} {...filterProps} />
      </FilterMenuButton>
      <FilterMenuButton
        label="Training Format"
        Icon={StarBorderIcon}
        changed={locationChanged}
        onReset={() => {
          resetState('location');
        }}
        {...menuProps}
      >
        <TrainingFormatFilter 
          {...filterProps} 
          stateKey="location"
          options={[
            { value: 'in-person', label: 'In person only' },
            { value: 'remote', label: 'Virtual / Remote' },
            { value: 'hybrid', label: 'Hybrid' },
          ]}
        />
      </FilterMenuButton>
      {filterState.location !== 'remote' && (
        <FilterMenuButton
          label="Cities"
          Icon={StarBorderIcon}
          changed={citiesChanged}
          onReset={() => {
            resetState('cities');
          }}
          {...menuProps}
        >
          <ListFilter stateKey="cities" items={cities} {...filterProps} />
        </FilterMenuButton>
      )}
    </DesktopFilters>
  );
};

export const filterInitialState = {
  date1: undefined,
  date2: undefined,
  location: undefined,
  roles: [],
  cities: [],
};

const CareerDevMobileFilters = ({
  tempFilterState,
  setTempFilterState,
  resetState,
  setFilterState,
  cities,
  roles,
}) => {
  const [open, setOpen] = useState(false);
  const filterProps = {
    filterState: tempFilterState,
    setFilterState: setTempFilterState,
  };

  return (
    <MobileFilters
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setFilterState({ ...tempFilterState });
      }}
      onClear={() => {
        resetState();
        setOpen(false);
      }}
    >
      <MobileFilterMenu label="Dates">
        <DateFilter {...filterProps} />
      </MobileFilterMenu>
      <MobileFilterMenu label="Roles">
        <ListFilter stateKey="roles" items={roles} {...filterProps} />
      </MobileFilterMenu>
      <MobileFilterMenu label="Training Format">
        <TrainingFormatFilter 
          {...filterProps} 
          stateKey="location" 
          options={[
            { value: 'in-person', label: 'In person only' },
            { value: 'remote', label: 'Virtual / Remote' },
            { value: 'hybrid', label: 'Hybrid' },
          ]}
        />
      </MobileFilterMenu>
      <MobileFilterMenu label="Cities">
        <ListFilter stateKey="cities" items={cities} {...filterProps} />
      </MobileFilterMenu>
    </MobileFilters>
  );
};
